import React, { useEffect, useState, useCallback } from "react";
import { fetchReferralDetail, patchReferral } from "../../../services/referralService";
import { fetchCandidateDetail, fetchLinkedinExperience } from "../../../services/candidateService";
import { ReferralDetail, CandidateDetail, LinkedInExperienceResponse } from "../../../types/fyo";
import { useAuth } from "../../../context/useAuth";
import PageHeader from "../../shared/PageHeader/PageHeader";
import Badge from "../../shared/Badge/Badge";
import { formatDateLong } from "../../../utils/formatters";
import "./ReferralDetailPage.css";

interface Props {
  opportunityResourceId: string | number;
  onBack:                () => void;
  onViewCandidate:       (id: number) => void;
}

type ActionType = "resolve" | "close_loop" | null;

const TERMINAL_STATUSES = new Set(["placed", "not_placed", "cannot_be_tracked"]);

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fyo-detail-field">
      <span className="fyo-detail-field__label">{label}</span>
      <div className="fyo-detail-field__value">{children}</div>
    </div>
  );
}

export default function ReferralDetailPage({ opportunityResourceId, onBack, onViewCandidate }: Props) {
  const { email: currentUser } = useAuth();

  const [referral,    setReferral]    = useState<ReferralDetail | null>(null);
  const [candidate,   setCandidate]   = useState<CandidateDetail | null>(null);
  const [experience,  setExperience]  = useState<LinkedInExperienceResponse | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState<string | null>(null);

  // Action panel state
  const [activeAction, setActiveAction] = useState<ActionType>(null);
  const [actionStatus, setActionStatus] = useState<"placed" | "not_placed">("placed");
  const [actionReason, setActionReason] = useState("");
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError,  setActionError]  = useState<string | null>(null);

  const loadReferral = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchReferralDetail(opportunityResourceId)
      .then((r) => {
        setReferral(r);
        return Promise.all([
          fetchCandidateDetail(r.rms_candidate_id),
          fetchLinkedinExperience(r.rms_candidate_id),
        ]);
      })
      .then(([cand, exp]) => { setCandidate(cand); setExperience(exp); })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [opportunityResourceId]);

  // After an action only re-fetch the referral (candidate is read-only here)
  const refreshReferral = useCallback(() => {
    fetchReferralDetail(opportunityResourceId)
      .then(setReferral)
      .catch((e: any) => setActionError(e.message));
  }, [opportunityResourceId]);

  useEffect(() => { loadReferral(); }, [loadReferral]);

  const openAction = (type: ActionType) => {
    setActiveAction(type);
    setActionStatus("placed");
    setActionReason("");
    setActionError(null);
  };

  const closeAction = () => {
    setActiveAction(null);
    setActionError(null);
  };

  const handleResolve = async () => {
    if (!referral) return;
    setActionSaving(true);
    setActionError(null);
    try {
      await patchReferral(referral.id, {
        status:        actionStatus,
        status_reason: actionReason || null,
        manual_review: false,
        changed_by:    currentUser,
      });
      closeAction();
      refreshReferral();
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setActionSaving(false);
    }
  };

  const handleCloseLoop = async () => {
    if (!referral) return;
    setActionSaving(true);
    setActionError(null);
    try {
      await patchReferral(referral.id, {
        loop_closed:   true,
        status_reason: actionReason || null,
        changed_by:    currentUser,
      });
      closeAction();
      refreshReferral();
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setActionSaving(false);
    }
  };

  const windowExpired = referral?.tracking_window_end ? new Date(referral.tracking_window_end) < new Date() : false;

  return (
    <div className="fyo-page">
      <button className="fyo-back-btn" onClick={onBack}>← Back to Referrals</button>
      <PageHeader
        title={referral ? `Referral #${referral.opportunity_resource_id}` : "Referral Detail"}
        subtitle={referral?.candidate_name ?? ""}
      />

      {error && <div className="fyo-error-banner">{error}</div>}

      {loading ? (
        <p className="state-text state-text--muted">Loading...</p>
      ) : referral ? (
        <div className="fyo-refdetail__layout">

          {/* ── Referral Info ── */}
          <div className="fyo-card">
            <h2 className="fyo-section-title">Referral Info</h2>
            <div className="fyo-detail-grid">
              <Field label="RMS Opportunity Resource ID">{referral.opportunity_resource_id}</Field>
              <Field label="RMS Referred Company">{referral.referred_company || "—"}</Field>
              <Field label="RMS Referred Job Title">{referral.referred_job_title || "—"}</Field>
              <Field label="RMS Referral Date">{formatDateLong(referral.referral_date)}</Field>
              <Field label="Tracking End">
                <div className="fyo-detail-field__value-row">
                  {formatDateLong(referral.tracking_window_end)}
                  {windowExpired && <Badge text="expired" />}
                </div>
              </Field>
            </div>
          </div>

          {/* ── Candidate Card ── */}
          {candidate && (
            <div className="fyo-card">
              <h2 className="fyo-section-title">Candidate</h2>
              <div className="fyo-detail-grid">
                <Field label="RMS Candidate ID">
                  <button className="fyo-link-btn" onClick={() => onViewCandidate(candidate.rms_candidate_id)}>
                    {candidate.rms_candidate_id}
                  </button>
                </Field>
                <Field label="RMS Name">{candidate.display_name}</Field>
                <Field label="RMS Email">{referral.candidate_email || "—"}</Field>
                <Field label="RMS LinkedIn">
                  {candidate.linkedin_url
                    ? <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="fyo-link-btn">View Profile</a>
                    : <span className="fyo-muted">—</span>}
                </Field>
                <Field label="RMS Current Employer">{candidate.current_employer || "—"}</Field>
                <Field label="RMS State">{candidate.state || "—"}</Field>
                <Field label="RMS Country">{candidate.country || "—"}</Field>
              </div>
            </div>
          )}

          {/* ── Tracking Card ── */}
          <div className="fyo-card">
            <h2 className="fyo-section-title">Tracking</h2>

            <div className="fyo-detail-grid" style={{ marginBottom: "var(--space-5)" }}>
              <Field label="Status"><Badge text={referral.status} /></Field>
            </div>

            {/* ── Action Buttons ── */}
            <div className="fyo-scrape-actions">
              {referral.manual_review && (
                <button className="fyo-btn fyo-btn--primary fyo-btn--sm"
                  onClick={() => openAction("resolve")}>
                  Resolve Manual Review
                </button>
              )}
              {!referral.loop_closed && (referral.status === "placed" || referral.status === "not_placed") && (
                <button className="fyo-btn fyo-btn--ghost fyo-btn--sm"
                  onClick={() => openAction("close_loop")}>
                  Close Loop
                </button>
              )}
            </div>

            {/* ── Inline Action Panels ── */}
            {activeAction === "resolve" && (
              <div className="fyo-inline-panel">
                <span className="fyo-inline-panel__label">
                  Resolve manual review — choose outcome for <strong>{referral.candidate_name}</strong>
                </span>
                <select className="fyo-select" value={actionStatus} onChange={(e) => setActionStatus(e.target.value as any)}>
                  <option value="placed">Placed</option>
                  <option value="not_placed">Not Placed</option>
                </select>
                <textarea
                  className="fyo-textarea"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Reason (optional)"
                  rows={2}
                />
                {actionError && <span className="fyo-panel-error text-danger">{actionError}</span>}
                <div className="fyo-inline-panel__btns">
                  <button className="fyo-btn fyo-btn--primary fyo-btn--sm" disabled={actionSaving} onClick={handleResolve}>
                    {actionSaving ? "Saving..." : "Confirm"}
                  </button>
                  <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" onClick={closeAction}>Cancel</button>
                </div>
              </div>
            )}

            {activeAction === "close_loop" && (
              <div className="fyo-inline-panel">
                <span className="fyo-inline-panel__label">
                  Close loop for <strong>{referral.candidate_name}</strong> — {referral.referred_company || "Unknown company"}
                </span>
                <textarea
                  className="fyo-textarea"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Reason (optional)"
                  rows={2}
                />
                {actionError && <span className="fyo-panel-error text-danger">{actionError}</span>}
                <div className="fyo-inline-panel__btns">
                  <button className="fyo-btn fyo-btn--primary fyo-btn--sm" disabled={actionSaving} onClick={handleCloseLoop}>
                    {actionSaving ? "Saving..." : "Close Loop"}
                  </button>
                  <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" onClick={closeAction}>Cancel</button>
                </div>
              </div>
            )}

          </div>

          {/* ── LinkedIn Experience ── */}
          <div className="fyo-card">
            <h2 className="fyo-section-title">LinkedIn Experience</h2>
            {experience?.scraped_at && (
              <p className="fyo-refdetail__exp-subtitle">
                Last scraped: {formatDateLong(experience.scraped_at)}
              </p>
            )}
            {!experience || experience.experience.length === 0 ? (
              <div className="fyo-empty-card">No LinkedIn experience scraped yet</div>
            ) : (
              <div className="fyo-table-wrap">
              <table className="fyo-table fyo-table--compact">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Position</th>
                    <th>Location</th>
                    <th>Period</th>
                  </tr>
                </thead>
                <tbody>
                  {experience.experience.map((e, i) => {
                    return (
                      <tr key={i} className={i % 2 === 1 ? "fyo-table__row--odd" : ""}>
                        <td className="fyo-table__cell--primary">{e.company}</td>
                        <td>{e.position}</td>
                        <td>{e.location || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {e.start_date || "?"} → {e.end_date || "Present"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>

        </div>
      ) : null}
    </div>
  );
}
